import React, { useEffect, useState } from 'react'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import { connect } from 'react-redux'

import './styles.css'
import { apiFunction } from '../../apiCall/function'

/**
 * The exam dates currently open for booking.
 *
 * Read-only and public — shown to signed-out visitors too, so someone deciding
 * whether to register can see that dates exist before creating an account. Booking itself still happens in the
 * wizard, which applies the per-candidate rules this panel deliberately does
 * not know about (subject/centre mapping, special slots, existing allotments) —
 * so a date listed here is not a promise that this particular candidate can
 * book it. Exams without training only — with-training exams are booked
 * through special slots, which are per candidate.
 */
// 4 columns x 4 rows. The panel has a fixed height now, and this is what fits
// in it without the grid scrolling — beyond this the dates page instead.
const PER_PAGE = 16

const OpenDates = (props) => {
    const navigate = useNavigate()

    // Booking starts from the application screen, which only exists in the
    // signed-in route table; a signed-out visitor gets the login screen.
    const loggedIn = Boolean(props.auth?.auth?.token)

    const [dates, setDates] = useState([])
    const [loading, setLoading] = useState(true)
    const [failed, setFailed] = useState(false)
    const [updatedAt, setUpdatedAt] = useState(null)
    const [page, setPage] = useState(0)

    useEffect(() => {
        let cancelled = false

        const load = async (isRefresh) => {
            // Public endpoint — no token. Passing one would still work, but a
            // signed-out visitor has none and this must render for them too.
            const result = await apiFunction('form/open-dates', 'get', {}, false, false)

            if (cancelled) return

            // This portal's apiFunction returns the response BODY directly
            // (the admin panel's wraps it in {data, status} — they differ), so
            // the payload is result.data and success is result.code === 1.
            if (result?.code === 1 && Array.isArray(result.data)) {
                setDates(result.data)
                setFailed(false)
                setUpdatedAt(new Date())
            } else if (!isRefresh) {
                // A failed BACKGROUND refresh keeps the last good list on
                // screen rather than replacing live data with an error — the
                // seats shown are a few seconds stale at worst.
                setFailed(true)
            }
            setLoading(false)
        }

        load(false)

        // Seats move as other candidates book, so a page left open goes stale.
        // 30s is frequent enough that the count stays trustworthy without
        // making this a load-bearing poll on a public, unauthenticated
        // endpoint that every visitor hits.
        const timer = setInterval(() => load(true), 30000)

        // Refresh the moment the tab comes back to the foreground, which is
        // when a stale count is most likely to be acted on. Polling is skipped
        // while hidden — a backgrounded tab should not keep hitting the API for
        // a panel nobody is looking at.
        const onVisible = () => {
            if (document.visibilityState === 'visible') load(true)
        }
        document.addEventListener('visibilitychange', onVisible)

        // The homepage can be navigated away from while this is in flight;
        // setting state after that warns and leaks.
        return () => {
            cancelled = true
            clearInterval(timer)
            document.removeEventListener('visibilitychange', onVisible)
        }
    }, [])

    // Only slots that can actually be booked. A full or unmapped centre is
    // noise on a page whose whole purpose is "where can I sit the exam" — and
    // a date whose every centre is full drops out entirely rather than
    // rendering a header with nothing bookable under it.
    const bookable = dates.filter((row) => row.available)

    // Several centres can run the same date, and a candidate thinks in dates
    // first — so the rows are grouped by date with the centres nested under it.
    const grouped = bookable.reduce((acc, row) => {
        if (!acc[row.date]) acc[row.date] = []
        acc[row.date].push(row)
        return acc
    }, {})

    const orderedDates = Object.keys(grouped).sort()

    const pageCount = Math.max(1, Math.ceil(orderedDates.length / PER_PAGE))
    // The 30s refresh can shorten the list — a date filling up drops out — so
    // a page that no longer exists would render blank.
    const safePage = Math.min(page, pageCount - 1)
    const pageDates = orderedDates.slice(safePage * PER_PAGE, (safePage + 1) * PER_PAGE)

    if (loading) {
        return (
            <div className="open_dates">
                <h4 className="open_dates_title">Upcoming Exam Dates</h4>
                <p className="open_dates_empty">Loading…</p>
            </div>
        )
    }

    if (failed) {
        return (
            <div className="open_dates">
                <h4 className="open_dates_title">Upcoming Exam Dates</h4>
                <p className="open_dates_empty">
                    Could not load exam dates just now. Please try again shortly.
                </p>
            </div>
        )
    }

    if (orderedDates.length === 0) {
        return (
            <div className="open_dates">
                <h4 className="open_dates_title">Upcoming Exam Dates</h4>
                <p className="open_dates_empty">
                    {dates.length > 0
                        ? 'All upcoming exam dates are currently full. Seats open up as bookings change — please check back shortly.'
                        : 'No exam dates are open at the moment. Please check back later.'}
                </p>
            </div>
        )
    }

    return (
        <div className="open_dates">
            <h4 className="open_dates_title">Upcoming Exam Dates</h4>
            <p className="open_dates_sub">
                {orderedDates.length} date{orderedDates.length === 1 ? '' : 's'} with
                seats available.
                {updatedAt ? (
                    <span className="open_dates_stamp">
                        {' '}Last updated {updatedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    </span>
                ) : null}
            </p>

            <div className="open_dates_grid">
                {pageDates.map((date) => {
                    // Seats across every centre running this date. One number
                    // per date keeps a row to a single line; the per-centre
                    // split lives in the tooltip rather than a nested list.
                    const seats = grouped[date].reduce((n, c) => n + c.remaining, 0)
                    const centres = grouped[date].map((c) => `${c.center}: ${c.remaining} left`).join(' · ')

                    return (
                        <div className="open_dates_chip" key={date} title={centres}>
                            <span className="open_dates_chip_date">
                                {moment(date, 'YYYY-MM-DD').format('DD MMM')}
                            </span>
                            <span className="open_dates_chip_seats">{seats} left</span>
                        </div>
                    )
                })}
            </div>

            {pageCount > 1 ? (
                <div className="open_dates_pager">
                    <button
                        type="button"
                        className="open_dates_arrow"
                        onClick={() => setPage(safePage - 1)}
                        disabled={safePage === 0}
                        aria-label="Previous dates"
                    >
                        ‹
                    </button>

                    {/* The count is what makes the hidden dates discoverable —
                        arrows and dots alone did not tell the candidate there
                        were more dates to see. */}
                    <span className="open_dates_more">
                        {safePage < pageCount - 1
                            ? `+${orderedDates.length - (safePage + 1) * PER_PAGE} more`
                            : `${orderedDates.length} dates`}
                    </span>

                    <div className="open_dates_dots">
                        {Array.from({ length: pageCount }).map((_, i) => (
                            <button
                                type="button"
                                key={i}
                                className={`open_dates_dot ${i === safePage ? 'is_active' : ''}`}
                                onClick={() => setPage(i)}
                                aria-label={`Dates page ${i + 1} of ${pageCount}`}
                            />
                        ))}
                    </div>

                    <button
                        type="button"
                        className="open_dates_arrow"
                        onClick={() => setPage(safePage + 1)}
                        disabled={safePage >= pageCount - 1}
                        aria-label="More dates"
                    >
                        ›
                    </button>
                </div>
            ) : null}

            <button
                type="button"
                className="open_dates_cta"
                onClick={() => navigate(loggedIn ? '/application' : '/login')}
            >
                {loggedIn ? 'Book a Slot' : 'Login to Book'}
            </button>
        </div>
    )
}

const mapStateToProps = (state) => {
    return {
        auth: state.Auth
    }
}

export default connect(mapStateToProps)(OpenDates)
